<template>
  <div :class="$style.dropDown">
    <button
      v-on-clickaway="hideDropDownList"
      :class="$style.dropDownToggle"
      @click="toggleListDisplay"
    >
      <Avatar
        :class="$style.avatar"
        :email="user.email"
        :firstName="user.firstname"
        :lastName="user.lastname"
      />
    </button>
    <DropDownList
      :class="$style.dropDownList"
      :isLargeDropDown="false"
      itemSection="Main Header"
      :menuSections="menuSections"
      :showList="isListVisible"
    />
  </div>
</template>

<script>
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  LoginConsts,
} from 'client/util/Constants';
import { mixin as clickaway } from 'vue-clickaway';
import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapGetters, mapState } from 'vuex';
import AdministratorModal from 'client/components/Modal/Admin/AdministratorModal.vue';
import ApiSettingsModal from 'client/components/MainHeader/ApiSettingsModal.vue';
import Avatar from 'client/components/MainHeader/Avatar.vue';
import ChangePasswordModal from 'components/MainHeader/ChangePasswordModal.vue';
import DropDownList from 'components/DropDownMenu/DropDownList.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ModalEventBus from 'components/Modal/ModalEventBus';
import OptimizelyHelper from 'client/GrowthEngineering/OptimizelyHelper';
import SubmitIdeaModal from 'client/Support/SubmitIdeaModal.vue';
import Util from 'client/util/Util';

const {
  description, location, section, text, toUrl,
} = eventProperties;

const organizationsCollection = 'OrganizationsCollection';
const settingsModule = 'SettingsModule';

export default {
  name: 'UserMenu',

  components: {
    Avatar,
    DropDownList,
  },

  mixins: [clickaway],

  EventHelper,
  OptimizelyHelper,
  Util,

  props: {
    isProviderOrg: Boolean,
  },

  data: () => ({
    isListVisible: false,
  }),

  computed: {
    ...mapGetters(settingsModule, {
      hasBilling: 'hasBilling',
      isCommandRunner: 'isCommandRunner',
      isCustomer: 'getIsCustomer',
      isProvider: 'isProvider',
      user: 'getUser',
    }),

    ...mapGetters(organizationsCollection, {
      orgCreatedDate: 'getOrgCreatedDate',
    }),

    ...mapState(settingsModule, [
      'userNeedsPWReset',
      'userPassSet',
    ]),

    menuSections() {
      const adminMenuItem = {
        label: 'Administrators',
        action: '#/settings/administrators',
        addSeparator: true,
      };

      const accountItem = {
        label: 'Update/Add Payment Info',
        action: this.openAccountsPage,
        testType: 'page',
      };

      const menuItems = [
        {
          label: this.user.email,
          static: true,
        },
        {
          label: 'Change Password',
          action: this.changePassword,
          addSeparator: true,
        },

        {
          label: 'Launch User Portal',
          action: LoginConsts.loginRoutes.user,
          external: true,
          showExternalLinkIcon: true,
          rightAlignExternalLinkIcon: true,
        },
        // The admin should have access to the "Administrators" menu item
        !this.isCommandRunner ? adminMenuItem : undefined,

        this.showAccountLink ? accountItem : undefined,

        {
          label: 'My API Key',
          action: this.openApiKeyModal,
        },

        {
          label: 'Log Out',
          action: '#/logout',
          addSeparator: true,
        },
      ];

      return [{ menuItems: menuItems.filter(menuItem => menuItem) }];
    },

    showAccountLink() {
      return this.hasBilling
        && !this.isProviderOrg
        && !this.isProvider;
    },
  },

  created() {
    if (this.userNeedsPWReset) {
      this.changePassword();
    }
  },

  methods: {
    changePassword() {
      ModalEventBus.open({
        component: ChangePasswordModal,
        preventCloseOnOverlayClick: this.userNeedsPWReset,
      });
    },

    hideDropDownList() {
      this.isListVisible = false;
    },

    openAccountsPage() {
      this.$options.EventHelper.triggerLinkClicked({
        description: 'Opens the account billing page.',
        location: eventLocations.upperNavigation,
        section: 'User Dropdown Menu',
        text: 'Update/Add Payment Info',
      });

      this.$options.Util.redirect(ItemTypeRoutes[ItemTypeConsts.accountBilling]);
    },

    openAdministratorsModal() {
      this.$options.EventHelper.triggerAdminDropdownButtonClicked();
      ModalEventBus.open({
        component: AdministratorModal,
        showClose: false,
        isLargeModal: true,
      });
    },

    openApiKeyModal() {
      ModalEventBus.open({
        component: ApiSettingsModal,
      });
    },

    openPricingPage() {
      this.$options.EventHelper.triggerLinkClicked({
        [description]: 'Opens the pricing page.',
        [location]: eventLocations.upperNavigation,
        [section]: 'User Dropdown Menu',
        [text]: 'Pricing',
        [toUrl]: ItemTypeRoutes[ItemTypeConsts.pricing],
      });

      this.$options.Util.redirect(ItemTypeRoutes[ItemTypeConsts.pricing]);
    },

    submitIdea() {
      ModalEventBus.open({
        component: SubmitIdeaModal,
        isLargeModal: true,
      });
    },

    toggleListDisplay() {
      this.isListVisible = !this.isListVisible;
    },
  },
};
</script>

<style module>
.dropDown {
  position: relative;
}

.avatar {
  font-size: 32px;
}

.dropDownToggle {
  background: none;
  cursor: pointer;
  display: inline-block;
  padding: 0;
}

.dropDownList {
  max-width: 50ch;
  min-width: 20ch;
  right: 0;
  top: calc(100% + 0.5rem);
}
</style>
